import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigation, useParams } from 'react-router';

import { useQuery } from '@tanstack/react-query';

import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/lib/components/theme-provider';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { RatingErrorsContext } from '@/lib/layout/RatingErrorsContext';

import { glacierPolicyApiGetPolicyOptions } from '@/lib/heyapi/@tanstack/react-query.gen';
import { glacierPolicyApiGetLatestTransactionSummary } from '@/lib/heyapi/sdk.gen';
import type { FieldValidationError } from '@/lib/heyapi/types.gen';
import { TransactionStatus, TransactionType } from '@/lib/heyapi/types.gen';

import { MaintenanceBanner } from '../components/LayoutBanner/MaintenanceBanner';
import Loader from '../components/Loader';
import { useInitializePermissionsStore } from '../stores/useInitializePermissionsStore';
import { setFullStoryPolicyContext } from '../utils/fullstory';
import { useAgentAuthorityErrors } from './AgentAuthorityErrorsContext';
import { CurrentTransactionContext } from './CurrentTransactionContext';
import { useGlobalLoading } from './GlobalLoadingContext';
import { Header } from './Header/index';
import Meta from './Meta';
import Sidebar from './Sidebar';
import type { userRole } from './types';
import { userRoleEnum } from './types';
import { useAgentAuthority } from './useAgentAuthority';
import { useEditMode } from './useEditMode';

const Layout = () => {
  const { id } = useParams();
  const navigation = useNavigation();
  const { globalLoading } = useGlobalLoading();
  const { agentAuthorityErrors } = useAgentAuthorityErrors();

  //send the policy id to fullstory for context
  useEffect(() => {
    if (id) setFullStoryPolicyContext(id);
  }, [id]);

  // initialize the permissions store with the policy id
  const { isPolicyReferredToMe, currentUser } = useInitializePermissionsStore({
    policyId: id || undefined,
  });

  const [errors, setErrors] = useState<FieldValidationError[] | null>(null);
  const { editMode: permissionsAllowEdit, setEditMode } = useEditMode(id);

  const isLoading = navigation.state === 'loading' || globalLoading;
  const role = (currentUser?.role as userRole) || null;
  const { agentAuthority, refetchAgentAuthority } = useAgentAuthority({
    policyId: id!,
    currentUser,
  });

  const { data: transactionData } = useQuery({
    queryKey: ['latestTransactionSummary', id],
    queryFn: () =>
      glacierPolicyApiGetLatestTransactionSummary({
        path: {
          account_id: id!,
        },
      }),
    enabled: id !== undefined,
  });

  // DC-3069 route (b): TransactionSummaryData does NOT carry a transaction
  // number. Source currentTransactionNumber from the already-loaded policy
  // query (Policy.latestTransaction.transactionNumber) — the same query the
  // Sidebar uses, so this shares the react-query cache (no extra request).
  const { data: policyData } = useQuery({
    ...glacierPolicyApiGetPolicyOptions({
      path: {
        account_id: id!,
      },
    }),
    enabled: id !== undefined,
  });

  // current transaction data
  const transactionInfo = useMemo(() => {
    const data = transactionData?.data;
    const currentTransactionStatus = data?.currentTransactionStatus || null;
    const currentTransactionType = data?.currentTransactionType || null;
    return {
      currentTransactionStatus,
      currentTransactionType,
      currentTransactionEffectiveDate:
        data?.currentTransactionEffectiveDate || null,
      isChangeExpirationDateMidterm:
        data?.isChangeExpirationDateMidterm ?? false,
      // DC-3069: an in-progress policy endorsement is a midterm endorsement.
      isMidtermEndorsement:
        currentTransactionType === TransactionType.POLICY_ENDORSEMENT &&
        currentTransactionStatus === TransactionStatus.IN_PROGRESS,
      currentTransactionNumber:
        policyData?.latestTransaction?.transactionNumber ?? null,
    };
  }, [transactionData?.data, policyData?.latestTransaction?.transactionNumber]);

  // A Change Expiration Date midterm is a date-only endorsement; lock the
  // entire policy to read-only while one is in progress so the underwriter
  // can't accidentally widen the scope of the change.
  const isCedInProgress =
    transactionInfo.isChangeExpirationDateMidterm &&
    transactionInfo.currentTransactionStatus === TransactionStatus.IN_PROGRESS;
  const editMode = permissionsAllowEdit && !isCedInProgress;

  const agentAuthorityContextValue = useMemo(
    () => ({
      editMode,
      permissionsAllowEdit,
      setEditMode,
      agentAuthority,
      refetchAgentAuthority,
    }),
    [
      editMode,
      permissionsAllowEdit,
      setEditMode,
      agentAuthority,
      refetchAgentAuthority,
    ]
  );

  const transactionContextValue = useMemo(
    () => transactionInfo,
    [transactionInfo]
  );

  const ratingErrorsContextValue = useMemo(
    () => ({ errors, setErrors }),
    [errors, setErrors]
  );

  const isAgent = role === userRoleEnum.AGENT;

  return (
    <ThemeProvider>
      <RatingErrorsContext.Provider value={ratingErrorsContextValue}>
        <AgentAuthorityContext.Provider value={agentAuthorityContextValue}>
          <CurrentTransactionContext.Provider value={transactionContextValue}>
            <Meta />
            {isLoading ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50">
                <Loader />
              </div>
            ) : null}
            {/* TODO: Remove overflow-hidden and implement responsive layout */}
            <div className="flex h-screen overflow-hidden">
              {/* Fixed Sidebar */}
              <aside className="fixed left-0 top-0 z-30 h-screen w-[20vw] flex-shrink-0">
                <Sidebar />
              </aside>

              {/* Main content area with margin for fixed sidebar */}
              <div className="ml-[20vw] flex h-full flex-1 flex-col">
                {/* Header - normal, not sticky */}
                <MaintenanceBanner />
                <header className="flex-shrink-0 border-b bg-white">
                  <Header />
                </header>
                {/* Main Content - takes remaining height and scrolls internally */}
                <main className="relative flex flex-1 flex-col overflow-auto">
                  <Suspense fallback={<Loader />}>
                    <Outlet />
                  </Suspense>
                  {errors?.length ? (
                    <Link
                      to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                      className="fixed bottom-4 left-[21vw] rounded bg-red-500 px-4 py-2 text-white"
                    >
                      Go To Rating Errors Page
                    </Link>
                  ) : null}
                  {/* DC-2518: Show button if user is Agent OR if the policy is assigned to current user */}
                  {agentAuthorityErrors?.length &&
                  (isAgent || isPolicyReferredToMe) ? (
                    <Link
                      to={`/policy/${id}/policy-decisions?tab=agentAuthorityErrors`}
                      className="fixed bottom-16 left-[21vw] rounded bg-red-500 px-4 py-2 text-white"
                    >
                      Go To Agent Authority Alerts
                    </Link>
                  ) : null}
                </main>
                <Toaster />
              </div>
            </div>
          </CurrentTransactionContext.Provider>
        </AgentAuthorityContext.Provider>
      </RatingErrorsContext.Provider>
    </ThemeProvider>
  );
};

export default Layout;
